import { NewPage } from "@certa/icons/components/NewPage";
import type { ModelledAPIResponse } from "@certa/queries/types/globalSearch.types";
import {
  transformHighlights,
  transformSearchResult
} from "../utils/searchTransformers";
import { IconContainer, WorkflowLogo } from "./Logo";
import { Database } from "@certa/icons/components/Database";

import React from "react";
import {
  Ellipsis,
  Typography,
  TypographyColors,
  TypographyVariants
} from "@certa/catalyst/components/Typography";
import { Box } from "@certa/catalyst/layouts/Box";
import { Skeleton, SkeletonGroup } from "@certa/catalyst/components/Skeleton";
import { classNames } from "@certa/catalyst/utils/common";
import { Stack } from "@certa/catalyst/layouts/Stack";
import { searchResultItemStyles as styles } from "./SearchResultItem.styles";
import type { SearchItem } from "../GlobalSearchWithSDO.schema";
import { SearchHighlighter } from "../../GlobalSearch/SearchHighlighter";

const getRandomWidth = (min: number, max: number): `${number}%` => {
  return `${Math.floor(Math.random() * (max - min + 1) + min)}%`;
};

export const SearchResultItemSkeleton = () => {
  const titleWidth = getRandomWidth(65, 95);
  const descriptionWidth = getRandomWidth(40, 60);
  return (
    <Box
      width="100%"
      borderBottom="1px solid var(--colors-neutral-400)"
      padding="4 0 4 0"
    >
      <div className={styles.container()}>
        <Skeleton width="2rem" height="2rem" variant="circular" />
        <Stack
          direction="vertical"
          gap="4"
          dangerouslySetClassName={styles.titleContainer}
        >
          <Skeleton width={titleWidth} height="0.75rem" />
          <Skeleton width={descriptionWidth} height="0.75rem" />
        </Stack>
      </div>
    </Box>
  );
};

const InternalSearchResultsLoading = () => (
  <SkeletonGroup label="Loading search results">
    {Array.from({ length: 40 }).map((_, index) => (
      <SearchResultItemSkeleton key={index} />
    ))}
  </SkeletonGroup>
);

export const SearchResultsLoading = React.memo(InternalSearchResultsLoading);

type SearchResultItemProps = {
  title: string | React.ReactNode;
  description?: string;
  isSelected?: boolean;
  searchItem: SearchItem;
  subDescription?: React.ReactNode;
  isTitleHighlighted?: boolean;
  isSubTitleHighlighted?: boolean;
};

export const ResultItemInner: React.FC<SearchResultItemProps> = ({
  title,
  description,
  isSelected,
  searchItem,
  subDescription,
  isTitleHighlighted,
  isSubTitleHighlighted
}) => {
  const icon =
    searchItem.type === "workflow" || searchItem.type === "field" ? (
      <WorkflowLogo
        logoUrl={"logo" in searchItem ? searchItem.logo : undefined}
        name={searchItem.title}
      />
    ) : (
      <IconContainer
        icon={
          searchItem.type === "object" ? (
            <Database size={16} />
          ) : (
            <NewPage size={16} />
          )
        }
      />
    );

  return (
    <Box width="100%">
      <div
        className={classNames(
          styles.container(isSelected),
          isSelected ? "selected" : ""
        )}
      >
        {icon}
        <div className={styles.titleContainer}>
          <Typography
            variant={TypographyVariants.LABEL_SM}
            color={
              isTitleHighlighted
                ? TypographyColors.NEUTRAL_600
                : TypographyColors.NEUTRAL_800
            }
            className={styles.title}
          >
            {title}
          </Typography>

          {description && (
            <Typography
              variant={TypographyVariants.LABEL_SM}
              color={TypographyColors.NEUTRAL_600}
            >
              <Ellipsis>{description}</Ellipsis>
            </Typography>
          )}
          {subDescription && (
            <Typography
              variant={TypographyVariants.LABEL_SM}
              color={
                isSubTitleHighlighted
                  ? TypographyColors.NEUTRAL_600
                  : TypographyColors.NEUTRAL_800
              }
            >
              {subDescription}
            </Typography>
          )}
        </div>
      </div>
    </Box>
  );
};

type ResultItemProps = {
  item: ModelledAPIResponse.SearchResult;
  isSelected: boolean;
};

export const ResultItem = ({ item, isSelected }: ResultItemProps) => {
  if (!item) return null;

  const searchItem = transformSearchResult(item);
  const { text, shouldShow, matches } = transformHighlights(item);

  if (!searchItem) return null;

  const subDescription = shouldShow ? text : null;

  const highlightedSubDescription =
    searchItem.type === "file" ? (
      <SearchHighlighter
        textToHighlight={subDescription || ""}
        highlights={matches}
        className={styles.highlight}
      />
    ) : (
      subDescription
    );

  const highlightedTitle = searchItem.title;

  return (
    <ResultItemInner
      title={highlightedTitle}
      description={searchItem.description}
      searchItem={searchItem}
      isSelected={isSelected}
      subDescription={highlightedSubDescription}
      isTitleHighlighted={
        searchItem.type === "field" ||
        (searchItem.type === "object" && shouldShow)
      }
      isSubTitleHighlighted={!shouldShow || searchItem.type === "file"}
    />
  );
};
